import { BaseComponent, Building } from '../types/protocols';
import { findBlueprintImage } from './blueprintLookup';
import { photoLocationLabel, photoLocationList } from './photoLocations';
import { roomDrawingMarker } from './componentRoomMarker';

export type LocationType = 'photo' | 'map' | 'blueprint';

/**
 * Data required to display a location marker on an image.
 */
export interface LocationData {
    /** Type of location - affects icon and default label */
    type: LocationType;
    /** Display label for the location */
    label: string;
    /** Firebase storage path to the image */
    storagePath: string;
    /** X coordinate of the marker in original image pixels */
    x: number;
    /** Y coordinate of the marker in original image pixels */
    y: number;
    /** Original image width in pixels */
    width: number;
    /** Original image height in pixels */
    height: number;
    /** Optional: Level name for blueprint locations */
    levelName?: string;
    /** Map image key or drawing key the marker sits on (map and blueprint locations). */
    imageKey?: string;
    /** The surface whose photo it is (photo locations). */
    parentKey?: string;
}

/**
 * A component's position markers (photo, map, blueprint) as LocationData
 * for the shared LocationDisplay, in display order: photo pins (primary first),
 * the current satellite map, the current level/roof drawing, then the room's
 * pin when the component has none of its own. Storage paths are resolved from
 * the full building model: map images live under `building.model.mapImages`,
 * blueprints under each level. Empty when the component carries no positions.
 */
export const componentLocations = (component: BaseComponent, building: Building): LocationData[] => {
    const result: LocationData[] = [];

    // Photo locations: one marker per pinned photo, primary pin first, side in the label
    for (const { location: photoLoc, side } of photoLocationList(component, building)) {
        result.push({
            type: 'photo',
            label: photoLocationLabel(side),
            storagePath: photoLoc.storagePath,
            x: photoLoc.x,
            y: photoLoc.y,
            width: photoLoc.width,
            height: photoLoc.height,
            parentKey: photoLoc.parentKey,
        });
    }

    // Map locations: only the pin on the building's current satellite image
    // (`currentMapImageKey`); pins on replaced images are not shown.
    if (component.mapLocations && building.model?.mapImages) {
        const mapImages = building.model.mapImages;
        const currentMapKey = building.model.currentMapImageKey;
        for (const [mapKey, mapLoc] of Object.entries(component.mapLocations)) {
            if (mapKey !== currentMapKey) continue;
            const mapImage = mapImages[mapKey];
            if (mapImage?.storagePath) {
                result.push({
                    type: 'map',
                    label: 'Position på satellitbild',
                    storagePath: mapImage.storagePath,
                    x: mapLoc.x,
                    y: mapLoc.y,
                    width: mapLoc.width,
                    height: mapLoc.height,
                    imageKey: mapKey,
                });
            }
        }
    }

    // Blueprint locations: on a level's drawing or on the roof drawing ("Position på takplan").
    // Only the owner's current drawing counts; a marker left on a replaced drawing is not shown.
    if (component.blueprintLocations) {
        for (const [blueprintKey, bpLoc] of Object.entries(component.blueprintLocations)) {
            const found = findBlueprintImage(building.model?.structure, blueprintKey);
            if (!found?.isCurrent || !found.image.storagePath) continue;
            const levelName = found.owner.kind === 'roofPlan' ? 'takplan' : found.label;
            result.push({
                type: 'blueprint',
                label: `Position på ${levelName}`,
                storagePath: found.image.storagePath,
                x: bpLoc.x,
                y: bpLoc.y,
                width: bpLoc.width,
                height: bpLoc.height,
                levelName: found.label,
                imageKey: blueprintKey,
            });
        }
    }

    // No pin of its own on its room's level drawing: the room's pin, captioned with the room.
    const roomMarker = roomDrawingMarker(component, building);
    if (roomMarker) {
        result.push({
            type: 'blueprint',
            label: `${roomMarker.roomLabel} på ${roomMarker.levelLabel}`,
            storagePath: roomMarker.image.storagePath,
            x: roomMarker.location.x,
            y: roomMarker.location.y,
            width: roomMarker.location.width,
            height: roomMarker.location.height,
            levelName: roomMarker.roomLabel,
            imageKey: roomMarker.imageKey,
        });
    }

    return result;
};
